import { useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GridColDef } from "@mui/x-data-grid";
import { DateRangeFilter, ServerDataGrid, formatCurrency, formatDate, useDataGridState } from "@safrater/ui";
import { useAuth } from "../../api/auth-context";
import { api } from "../../api/client";

interface PaymentRow {
  id: string;
  amount: string;
  dueDate: string;
  paidAt: string | null;
  status: "pending" | "awaiting_review" | "approved" | "rejected";
  payer: { id: string; name: string; email: string };
  institution: { id: string; name: string };
  license: { module: { name: string; key: string }; licenseType: { name: string } };
}

const STATUS_LABEL: Record<PaymentRow["status"], { label: string; color: "default" | "warning" | "success" | "error" }> = {
  pending: { label: "Pendente", color: "default" },
  awaiting_review: { label: "Aguardando revisão", color: "warning" },
  approved: { label: "Aprovado", color: "success" },
  rejected: { label: "Recusado", color: "error" },
};

export function PaymentsPage() {
  const state = useDataGridState();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [rejecting, setRejecting] = useState<PaymentRow | null>(null);
  const [reason, setReason] = useState("");

  const { data, isFetching } = useQuery({
    queryKey: ["payments", state.queryParams],
    queryFn: async () => (await api.get<{ data: PaymentRow[]; total: number }>("/payments", { params: state.queryParams })).data,
    placeholderData: (prev) => prev,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["payments"] });
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/payments/${id}/approve`),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/payments/${id}/reject`, { reason }),
    onSuccess: () => { invalidate(); setRejecting(null); setReason(""); },
  });

  const columns: GridColDef<PaymentRow>[] = [
    { field: "payer", headerName: "Pagador", flex: 1, minWidth: 200, sortable: false, valueGetter: (_v, row) => row.payer.name },
    { field: "institution", headerName: "Instituição", flex: 1, minWidth: 200, sortable: false, valueGetter: (_v, row) => row.institution.name },
    { field: "license", headerName: "Módulo", flex: 1, minWidth: 180, sortable: false, valueGetter: (_v, row) => row.license.module.name },
    {
      field: "amount", headerName: "Valor", width: 140, align: "right", headerAlign: "right", type: "number",
      valueGetter: (v) => Number(v as string),
      valueFormatter: (v) => formatCurrency(Number(v as number)),
    },
    { field: "dueDate", headerName: "Vencimento", width: 130, type: "date", valueGetter: (v) => (v ? new Date(v as string) : null), valueFormatter: (v) => (v ? formatDate((v as Date).toISOString()) : "") },
    {
      field: "status", headerName: "Status", width: 180,
      renderCell: ({ value }) => {
        const s = STATUS_LABEL[value as PaymentRow["status"]];
        return <Chip size="small" label={s.label} color={s.color} />;
      },
    },
    {
      field: "_actions", headerName: "Ações", width: 220, sortable: false, filterable: false,
      renderCell: ({ row }) =>
        user?.isMasterAdmin && row.status === "awaiting_review" ? (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={() => approve.mutate(row.id)}>Aprovar</Button>
            <Button size="small" color="error" onClick={() => setRejecting(row)}>Recusar</Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Pagamentos</Typography>
      <Box sx={{ mb: 2 }}>
        <DateRangeFilter
          value={state.dateRange ?? { start: null, end: null }}
          onChange={state.setDateRange}
          startLabel="Vencimento de"
          endLabel="Até"
        />
      </Box>
      <Box sx={{ height: 640 }}>
        <ServerDataGrid<PaymentRow>
          gridId="central.payments"
          userScope={user?.sub}
          rows={data?.data ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isFetching}
          paginationModel={state.pagination}
          onPaginationModelChange={state.setPagination}
          sortModel={state.sort}
          onSortModelChange={state.setSort}
          filterModel={state.filter}
          onFilterModelChange={state.setFilter}
        />
      </Box>

      <Dialog
        open={!!rejecting}
        onClose={() => { setRejecting(null); setReason(""); }}
        fullWidth maxWidth="sm"
      >
        <DialogTitle>Recusar pagamento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Informe o motivo da recusa. Será exibido ao pagador.
          </Typography>
          <TextField
            label="Motivo"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
            fullWidth multiline rows={3} required autoFocus
            helperText={`${reason.length}/500`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejecting(null); setReason(""); }}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!reason.trim() || reject.isPending}
            onClick={() => rejecting && reject.mutate({ id: rejecting.id, reason: reason.trim() })}
          >
            Recusar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
